import * as THREE from 'three';
import store from '../../redux/store';
import {
  selected_object,
  setPushIntersectedObject,
  addSceneMesh,
} from '../../redux/Slice/gridSlice';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import {
  startAnimation,
  stopAnimation,
  pauseAnimation,
} from './animationControls';
import { setLifeTimeout, clearLifeTimeout } from './lifeUtils';

/** Persist random facing; wind only adjusts Z each frame (avoids euler drift / flips). */
function setPlantBaseFacing(plant) {
  if (plant.userData.baseRotationY === undefined) {
    plant.userData.baseRotationY = plant.rotation.y;
  }
}

export const FOLIAGE_TREE_COLOR_DEFAULTS = {
  pine: '#1a4d2e',
  oak: '#228b22',
  birch: '#9acd32',
};

export const FOLIAGE_GRASS_COLOR_DEFAULT = '#228b22';
export const FOLIAGE_BUSH_COLOR_DEFAULT = '#228b22';

export const FOLIAGE_ANIMATION_DEFAULTS = {
  windStrength: 0.5,
  swaySpeed: 2,
  rustleSpeed: 4,
  waveSpeed: 5,
};

function pickFoliageNumber(source, key, fallback) {
  const value = source?.[key];
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Normalize wind/sway values — never treat particle intensity (e.g. 50) as wind strength. */
export function resolveFoliageAnimationConfig(source = {}) {
  let windStrength = pickFoliageNumber(
    source,
    'windStrength',
    FOLIAGE_ANIMATION_DEFAULTS.windStrength
  );

  // Repair polluted userData where intensity (50) was stored as windStrength.
  if (windStrength > 2) {
    windStrength =
      windStrength <= 100
        ? windStrength / 100
        : FOLIAGE_ANIMATION_DEFAULTS.windStrength;
  }

  return {
    windStrength: Math.max(0, Math.min(2, windStrength)),
    swaySpeed: pickFoliageNumber(
      source,
      'swaySpeed',
      FOLIAGE_ANIMATION_DEFAULTS.swaySpeed
    ),
    rustleSpeed: pickFoliageNumber(
      source,
      'rustleSpeed',
      FOLIAGE_ANIMATION_DEFAULTS.rustleSpeed
    ),
    waveSpeed: pickFoliageNumber(
      source,
      'waveSpeed',
      FOLIAGE_ANIMATION_DEFAULTS.waveSpeed
    ),
  };
}

function getFoliageLeavesGroup(plant) {
  const leaves = plant?.userData?.leaves;
  if (leaves?.children && Array.isArray(leaves.children)) {
    return leaves;
  }

  const leavesGroup = plant?.children?.find((child) => child?.isGroup);
  if (leavesGroup && plant.userData) {
    plant.userData.leaves = leavesGroup;
  }
  return leavesGroup || null;
}

export function repairFoliageCloneReferences(foliageGroup) {
  if (!foliageGroup) return;

  foliageGroup.traverse((node) => {
    if (!node.userData) {
      node.userData = {};
    }

    if (node.userData.type === 'tree') {
      getFoliageLeavesGroup(node);
    }
  });
}

export function createFoliageAnimateFunction(foliageGroup) {
  return function animateFoliage() {
    if (!foliageGroup?.parent) {
      if (foliageGroup?.userData?.animationId) {
        cancelAnimationFrame(foliageGroup.userData.animationId);
        foliageGroup.userData.animationId = null;
      }
      return;
    }

    const { windStrength, swaySpeed, rustleSpeed, waveSpeed } =
      resolveFoliageAnimationConfig(foliageGroup.userData || {});
    const time = Date.now() * 0.001;
    const dynamicWindStrength =
      Math.sin(time * 0.3) * windStrength + windStrength;

    (foliageGroup.children || []).forEach((plant, index) => {
      if (!plant.userData) {
        plant.userData = {};
      }

      let baseY = plant.userData.baseRotationY;
      if (baseY === undefined) {
        baseY = plant.rotation.y;
        plant.userData.baseRotationY = baseY;
      }
      plant.rotation.x = 0;
      plant.rotation.y = baseY;

      if (plant.userData.type === 'tree') {
        const sway =
          Math.sin(time * swaySpeed + index * 0.1) * dynamicWindStrength * 0.1;
        plant.rotation.z = sway;

        const leavesGroup = getFoliageLeavesGroup(plant);
        if (leavesGroup?.children) {
          leavesGroup.children.forEach((leafLayer, layerIndex) => {
            if (!leafLayer.userData) {
              leafLayer.userData = {};
            }
            if (leafLayer.userData.foliageLeafBaseY === undefined) {
              leafLayer.userData.foliageLeafBaseY = leafLayer.rotation.y;
            }
            leafLayer.rotation.y =
              leafLayer.userData.foliageLeafBaseY +
              Math.sin(time + layerIndex) * 0.05;
            (leafLayer.children || []).forEach((leaf) => {
              if (!leaf.userData) {
                leaf.userData = {};
              }
              if (leaf.userData.foliageLeafBaseX === undefined) {
                leaf.userData.foliageLeafBaseX = leaf.rotation.x;
              }
              leaf.rotation.x =
                leaf.userData.foliageLeafBaseX +
                Math.sin(time * 3 + index + layerIndex) * 0.05;
            });
          });
        }
      } else if (plant.userData.type === 'bush') {
        const rustle = Math.sin(time * rustleSpeed + index) * 0.02;
        plant.rotation.z = rustle;
        const baseH =
          (plant.userData.baseScale ?? 1) * (foliageGroup.userData.size ?? 1);
        plant.scale.y = Math.max(
          0.02,
          baseH + Math.sin(time * 2 + index) * 0.05
        );
      } else if (plant.userData.type === 'grass') {
        const wave =
          Math.sin(time * waveSpeed + index * 0.2) * dynamicWindStrength * 0.3;
        plant.rotation.z = wave;
      }
    });

    foliageGroup.userData.animationId = requestAnimationFrame(animateFoliage);
  };
}

export function restartFoliageAnimation(foliageGroup) {
  if (!foliageGroup) return;

  const animate = createFoliageAnimateFunction(foliageGroup);
  foliageGroup.userData.animateFunction = animate;

  if (foliageGroup.userData.isStopped) {
    stopAnimation(foliageGroup, foliageGroup.name);
    return;
  }

  startAnimation(foliageGroup, animate, foliageGroup.name);
}

function resolveFoliageTreeColors(particleData) {
  const legacyColor = particleData?.color;
  return {
    pine: particleData?.pineColor || FOLIAGE_TREE_COLOR_DEFAULTS.pine,
    oak: particleData?.oakColor || FOLIAGE_TREE_COLOR_DEFAULTS.oak,
    birch: particleData?.birchColor || FOLIAGE_TREE_COLOR_DEFAULTS.birch,
    grassColor:
      particleData?.grassColor ||
      legacyColor ||
      FOLIAGE_GRASS_COLOR_DEFAULT,
    bushColor:
      particleData?.bushColor || legacyColor || FOLIAGE_BUSH_COLOR_DEFAULT,
    /** @deprecated use grassColor / bushColor */
    color:
      particleData?.grassColor ||
      legacyColor ||
      FOLIAGE_GRASS_COLOR_DEFAULT,
  };
}

function getTreeFoliageColor(treeType, particleData) {
  const colors = resolveFoliageTreeColors(particleData);
  const hex =
    treeType === 'pine'
      ? colors.pine
      : treeType === 'oak'
        ? colors.oak
        : treeType === 'birch'
          ? colors.birch
          : colors.oak;
  return new THREE.Color(hex);
}

function colorHexForFoliagePlant(topPlant, config) {
  if (topPlant.userData?.type === 'tree') {
    const treeType = topPlant.userData?.treeType;
    if (treeType === 'pine') return config.pineColor;
    if (treeType === 'oak') return config.oakColor;
    if (treeType === 'birch') return config.birchColor;
  }
  if (topPlant.userData?.type === 'bush') return config.bushColor;
  if (topPlant.userData?.type === 'grass') return config.grassColor;
  return config.bushColor;
}

function applyFoliageMaterialColors(foliageSystem, config) {
  const opacity = Number(config.opacity);

  foliageSystem.traverse((child) => {
    if (!child.isMesh || !child.material) return;

    let topPlant = child;
    while (topPlant.parent && topPlant.parent !== foliageSystem) {
      topPlant = topPlant.parent;
    }

    const plantType = topPlant.userData?.type;
    if (
      plantType !== 'tree' &&
      plantType !== 'bush' &&
      plantType !== 'grass'
    ) {
      return;
    }

    // Opacity applies to entire plant (trunk, leaves, bush, grass)
    child.material.opacity = opacity;
    child.material.transparent = opacity < 1.0;
    child.material.depthWrite = opacity >= 1.0;
    child.material.needsUpdate = true;

    // Leaf colors only — trunks keep their trunk material color
    if (plantType === 'tree' && child.userData?.foliagePart !== true) {
      return;
    }

    const hex = colorHexForFoliagePlant(topPlant, config);
    if (child.material.color) {
      child.material.color.set(hex);
    }
  });
}

const handleFoliage = (scene, particle) => {
  if (!scene) return;

  // Use dynamic properties (robust to string values like '0')
  const treeCount = particle?.treeCount !== undefined ? Number(particle.treeCount) : 10;
  const bushCount = particle?.bushCount !== undefined ? Number(particle.bushCount) : 10;
  const grassCount = particle?.grassCount !== undefined ? Number(particle.grassCount) : 50;
  const forestRadius = particle?.forestRadius || 3;
  const forestSpread = particle?.forestSpread || 40;
  const rangeVal = particle?.range !== undefined ? Number(particle.range) : 10;
  const areaRadius = Math.max(1, rangeVal * 2);
  const opacity = particle?.opacity !== undefined ? particle.opacity : 1.0;
  const size = particle?.size !== undefined ? Number(particle.size) : 1.0;
  const {
    windStrength,
    swaySpeed,
    rustleSpeed,
    waveSpeed,
  } = resolveFoliageAnimationConfig(particle);

  // Enhanced forest with multiple tree types and undergrowth
  const foliageGroup = new THREE.Group();
  const life = particle?.life || 0;

  const treeColors = resolveFoliageTreeColors(particle);
  const bushColor = new THREE.Color(treeColors.bushColor);
  const grassColor = new THREE.Color(treeColors.grassColor);

  // Create different tree types
  const treeTypes = ['pine', 'oak', 'birch'];

  if (treeCount > 0) {
    for (let i = 0; i < treeCount; i++) {
      const treeType = treeTypes[Math.floor(Math.random() * treeTypes.length)];
      const tree = createTree(treeType, particle);

      // Placement scaled by areaRadius (range * 2); more natural distribution
      const angle = (i / Math.max(1, treeCount)) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      const radius = Math.random() * areaRadius;
      const x = Math.cos(angle) * radius + (Math.random() - 0.5) * (areaRadius * 0.05);
      const z = Math.sin(angle) * radius + (Math.random() - 0.5) * (areaRadius * 0.05);

      tree.position.set(x, 0, z);
      tree.rotation.y = Math.random() * Math.PI * 2;
      setPlantBaseFacing(tree);

      const baseScale = 0.4 + Math.random() * 0.5;
      tree.userData.baseScale = baseScale;
      const finalScale = baseScale * size;
      tree.scale.set(finalScale, finalScale, finalScale);

      foliageGroup.add(tree);
    }
  }

  // Add bushes and undergrowth
  if (bushCount > 0) {
    for (let i = 0; i < bushCount; i++) {
      const bush = createBush(particle);
      // Use areaRadius for bush spread (slightly tighter than max radius)
      bush.position.set(
        (Math.random() - 0.5) * areaRadius * 1.2,
        0,
        (Math.random() - 0.5) * areaRadius * 1.2
      );
      bush.rotation.y = Math.random() * Math.PI * 2;
      setPlantBaseFacing(bush);
      const baseScale = 0.2 + Math.random() * 0.3;
      bush.userData.baseScale = baseScale;
      bush.scale.setScalar(baseScale * size);
      foliageGroup.add(bush);
    }
  }

  // Add grass patches
  if (grassCount > 0) {
    for (let i = 0; i < grassCount; i++) {
      const grass = createGrass(particle);
      // Grass spread uses a slightly wider band
      grass.position.set(
        (Math.random() - 0.5) * areaRadius * 1.8,
        0,
        (Math.random() - 0.5) * areaRadius * 1.8
      );
      grass.rotation.y = Math.random() * Math.PI * 2;
      setPlantBaseFacing(grass);
      const baseScale = 0.3 + Math.random() * 0.4;
      grass.userData.baseScale = baseScale;
      grass.scale.y = baseScale * size;
      foliageGroup.add(grass);
    }
  }

  foliageGroup.deleteType = 'particle';
  foliageGroup.objType = 'foliage';
  const uniqueId = `foliage_${Date.now()}`;

  // Set the name based on particle data or generate unique ID
  if (particle && particle.id) {
    foliageGroup.name = particle.id;
  } else if (particle && particle._id) {
    foliageGroup.name = particle._id;
  } else {
    foliageGroup.name = uniqueId;
  }

  foliageGroup.displayName = 'Foliage'; // For UI display
  foliageGroup.url = uniqueId;
  foliageGroup.asset_file_name = 'foliage';
  foliageGroup.file_name = 'foliage';
  foliageGroup.mesh_code = 'foliage';
  foliageGroup.glb_type = 'shapes';
  foliageGroup.createdAt = new Date().toISOString();
  foliageGroup.userData = {
    material_settings: {
      shader: 'Standard',
      albedo: treeColors.grassColor,
      opacity: opacity,
      roughness: 0.8,
      metalness: 0.0,
    },
    // Store dynamic properties for animation
    treeCount: treeCount,
    bushCount: bushCount,
    grassCount: grassCount,
    forestRadius: forestRadius,
    forestSpread: forestSpread,
    color: treeColors.grassColor,
    grassColor: treeColors.grassColor,
    bushColor: treeColors.bushColor,
    pineColor: treeColors.pine,
    oakColor: treeColors.oak,
    birchColor: treeColors.birch,
    opacity: opacity,
    size: size,
    windStrength: windStrength,
    swaySpeed: swaySpeed,
    rustleSpeed: rustleSpeed,
    waveSpeed: waveSpeed,
    // Add cleanup function to stop animation
    cleanup: () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    },
    life,
  };

  // Expose creation helpers so updateFoliage can rebuild when counts/range change
  foliageGroup.userData.createTree = createTree;
  foliageGroup.userData.createBush = createBush;
  foliageGroup.userData.createGrass = createGrass;
  foliageGroup.userData.range = rangeVal;

  scene.add(foliageGroup);
  store.dispatch(setPushIntersectedObject(foliageGroup));
  selectObjectInScene(foliageGroup.name);

  const animate = createFoliageAnimateFunction(foliageGroup);
  foliageGroup.userData.animateFunction = animate;

  // Start animation if particle is not stopped
  if (!particle?.isStopped) {
    startAnimation(foliageGroup, animate, foliageGroup.name);
    setLifeTimeout(
      foliageGroup,
      typeof life === 'number' ? life : parseFloat(life) || 0
    );
  }

  // Tree creation functions
  function createTree(type, particleData) {
    const tree = new THREE.Group();
    tree.userData.type = 'tree';
    tree.userData.treeType = type;

    if (type === 'pine') {
      return createPineTree(tree, particleData);
    } else if (type === 'oak') {
      return createOakTree(tree, particleData);
    } else {
      return createBirchTree(tree, particleData);
    }
  }

  function createPineTree(tree, particleData) {
    const treeOpacity =
      particleData?.opacity !== undefined ? particleData.opacity : opacity;
    const leafColor = getTreeFoliageColor('pine', particleData);
    const trunkGeometry = new THREE.CylinderGeometry(0.15, 0.25, 3, 8);
    const trunkMaterial = new THREE.MeshStandardMaterial({
      color: particleData?.trunkColor
        ? new THREE.Color(particleData.trunkColor)
        : new THREE.Color(0x3d2914),
      opacity: treeOpacity,
      transparent: treeOpacity < 1.0,
      roughness: 0.9,
    });
    const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
    trunk.position.y = 1.5;
    trunk.castShadow = true;
    tree.add(trunk);

    const leavesGroup = new THREE.Group();
    const layers = 5;

    for (let i = 0; i < layers; i++) {
      const radius = 1.2 - i * 0.15;
      const height = 1.5;
      const y = 2.5 + i * 0.4;

      const needleGeometry = new THREE.ConeGeometry(radius, height, 8);
      const needleMaterial = new THREE.MeshStandardMaterial({
        color: leafColor,
        opacity: treeOpacity,
        transparent: treeOpacity < 1.0,
        roughness: 0.8,
      });

      const needles = new THREE.Mesh(needleGeometry, needleMaterial);
      needles.position.y = y;
      needles.castShadow = true;
      needles.userData.foliagePart = true;
      leavesGroup.add(needles);
    }

    tree.add(leavesGroup);
    tree.userData.leaves = leavesGroup;
    return tree;
  }

  function createOakTree(tree, particleData) {
    const treeOpacity =
      particleData?.opacity !== undefined ? particleData.opacity : opacity;
    const leafColor = getTreeFoliageColor('oak', particleData);
    const trunkGeometry = new THREE.CylinderGeometry(0.3, 0.4, 2.5, 8);
    const trunkMaterial = new THREE.MeshStandardMaterial({
      color: particleData?.trunkColor
        ? new THREE.Color(particleData.trunkColor)
        : new THREE.Color(0x4a3428),
      opacity: treeOpacity,
      transparent: treeOpacity < 1.0,
      roughness: 0.9,
    });
    const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
    trunk.position.y = 1.25;
    trunk.castShadow = true;
    tree.add(trunk);

    const leavesGroup = new THREE.Group();
    const clusterCount = 8;

    for (let i = 0; i < clusterCount; i++) {
      const leafGeometry = new THREE.SphereGeometry(0.8, 8, 6);
      const leafMaterial = new THREE.MeshStandardMaterial({
        color: leafColor,
        opacity: treeOpacity,
        transparent: treeOpacity < 1.0,
        roughness: 0.7,
      });

      const leafCluster = new THREE.Mesh(leafGeometry, leafMaterial);
      leafCluster.userData.foliagePart = true;
      const angle = (i / clusterCount) * Math.PI * 2;
      const radius = 1 + Math.random() * 0.5;

      leafCluster.position.set(
        Math.cos(angle) * radius,
        2.5 + Math.random() * 1,
        Math.sin(angle) * radius
      );
      leafCluster.scale.set(
        0.8 + Math.random() * 0.4,
        0.8 + Math.random() * 0.4,
        0.8 + Math.random() * 0.4
      );
      leafCluster.castShadow = true;
      leavesGroup.add(leafCluster);
    }

    tree.add(leavesGroup);
    tree.userData.leaves = leavesGroup;
    return tree;
  }

  function createBirchTree(tree, particleData) {
    const treeOpacity =
      particleData?.opacity !== undefined ? particleData.opacity : opacity;
    const leafColor = getTreeFoliageColor('birch', particleData);
    const trunkGeometry = new THREE.CylinderGeometry(0.1, 0.15, 4, 8);
    const trunkMaterial = new THREE.MeshStandardMaterial({
      color: particleData?.trunkColor
        ? new THREE.Color(particleData.trunkColor)
        : new THREE.Color(0xf5f5dc),
      opacity: treeOpacity,
      transparent: treeOpacity < 1.0,
      roughness: 0.6,
    });
    const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
    trunk.position.y = 2;
    trunk.castShadow = true;
    tree.add(trunk);

    const leavesGroup = new THREE.Group();
    const branchCount = 12;

    for (let i = 0; i < branchCount; i++) {
      const leafGeometry = new THREE.SphereGeometry(0.4, 6, 4);
      const leafMaterial = new THREE.MeshStandardMaterial({
        color: leafColor,
        opacity: treeOpacity,
        transparent: treeOpacity < 1.0,
        roughness: 0.6,
      });

      const leafCluster = new THREE.Mesh(leafGeometry, leafMaterial);
      leafCluster.userData.foliagePart = true;
      const angle = Math.random() * Math.PI * 2;
      const radius = 0.5 + Math.random() * 0.3;
      const height = 3 + Math.random() * 1.5;

      leafCluster.position.set(
        Math.cos(angle) * radius,
        height,
        Math.sin(angle) * radius
      );
      leafCluster.castShadow = true;
      leavesGroup.add(leafCluster);
    }

    tree.add(leavesGroup);
    tree.userData.leaves = leavesGroup;
    return tree;
  }

  function createBush() {
    const bush = new THREE.Group();
    bush.userData.type = 'bush';
    const bushOpacity = opacity;

    const leafCount = 5;
    for (let i = 0; i < leafCount; i++) {
      const leafGeometry = new THREE.SphereGeometry(0.5, 8, 6);
      const leafMaterial = new THREE.MeshStandardMaterial({
        color: bushColor,
        opacity: bushOpacity,
        transparent: bushOpacity < 1.0,
        roughness: 0.8,
      });

      const leaf = new THREE.Mesh(leafGeometry, leafMaterial);
      leaf.userData.foliagePart = true;
      leaf.position.set(
        (Math.random() - 0.5) * 1.5,
        Math.random() * 0.8,
        (Math.random() - 0.5) * 1.5
      );
      leaf.scale.set(
        0.6 + Math.random() * 0.4,
        0.6 + Math.random() * 0.4,
        0.6 + Math.random() * 0.4
      );
      leaf.castShadow = true;
      bush.add(leaf);
    }

    return bush;
  }

  function createGrass() {
    const grass = new THREE.Group();
    grass.userData.type = 'grass';
    const grassOpacity = opacity;

    const bladeCount = 3 + Math.floor(Math.random() * 5);

    for (let i = 0; i < bladeCount; i++) {
      const bladeGeometry = new THREE.CylinderGeometry(0.01, 0.02, 0.5, 4);
      const bladeMaterial = new THREE.MeshStandardMaterial({
        color: grassColor,
        opacity: grassOpacity,
        transparent: grassOpacity < 1.0,
        roughness: 0.7,
      });

      const blade = new THREE.Mesh(bladeGeometry, bladeMaterial);
      blade.position.set(
        (Math.random() - 0.5) * 0.2,
        0.25,
        (Math.random() - 0.5) * 0.2
      );
      blade.rotation.z = (Math.random() - 0.5) * 0.3;
      blade.scale.y = 0.5 + Math.random() * 0.5;
      blade.castShadow = true;
      grass.add(blade);
    }

    return grass;
  }

  // Add cleanup method to the foliage group
  foliageGroup.cleanup = () => {
    if (animationId) {
      cancelAnimationFrame(animationId);
      animationId = null;
    }
    // Clean up all children
    foliageGroup.children.forEach((child) => {
      if (child.userData && child.userData.cleanup) {
        child.userData.cleanup();
      }
    });
  };

  // Set transform from particle data if provided
  if (particle?.position) {
    foliageGroup.position.set(
      particle.position.x || 0,
      particle.position.y || 0,
      particle.position.z || 0
    );
  }
  if (particle?.rotation) {
    foliageGroup.rotation.set(
      particle.rotation.x || 0,
      particle.rotation.y || 0,
      particle.rotation.z || 0
    );
  }
  if (particle?.scale) {
    foliageGroup.scale.set(
      particle.scale.x || 1,
      particle.scale.y || 1,
      particle.scale.z || 1
    );
  }

  store.dispatch(selected_object(foliageGroup));
};

const updateFoliage = (scene, foliageSystem, newParticleData) => {
  if (!scene || !foliageSystem || foliageSystem.objType !== 'foliage') {
    console.warn('Invalid foliage system or scene provided for update');
    return;
  }

  // Get current configuration
  const currentConfig = foliageSystem.userData || {};

  // Handle animation state changes
  if (
    typeof newParticleData?.isPlaying !== 'undefined' ||
    typeof newParticleData?.isStopped !== 'undefined'
  ) {
    const isPlaying =
      newParticleData?.isPlaying ?? currentConfig.isPlaying ?? true;
    const isStopped =
      newParticleData?.isStopped ?? currentConfig.isStopped ?? false;

    if (isStopped) {
      foliageSystem.userData.isStopped = true;
      foliageSystem.userData.isPlaying = false;
      stopAnimation(foliageSystem, foliageSystem.name);
    } else if (isPlaying) {
      foliageSystem.userData.isStopped = false;
      foliageSystem.userData.isPlaying = true;
      restartFoliageAnimation(foliageSystem);
    } else {
      foliageSystem.userData.isPlaying = false;
      pauseAnimation(foliageSystem, foliageSystem.name);
    }
  }

  // Merge current config with new particle data
  const updatedConfig = {
    treeCount:
      newParticleData?.treeCount !== undefined
        ? Number(newParticleData.treeCount)
        : currentConfig.treeCount !== undefined
          ? Number(currentConfig.treeCount)
          : 10,
    bushCount:
      newParticleData?.bushCount !== undefined
        ? Number(newParticleData.bushCount)
        : currentConfig.bushCount !== undefined
          ? Number(currentConfig.bushCount)
          : 10,
    grassCount:
      newParticleData?.grassCount !== undefined
        ? Number(newParticleData.grassCount)
        : currentConfig.grassCount !== undefined
          ? Number(currentConfig.grassCount)
          : 50,
    // Keep backward-compatible forest params but prefer 'range' from UI
    range: newParticleData?.range ?? currentConfig.range ?? 10,
    forestRadius:
      newParticleData?.forestRadius || currentConfig.forestRadius || 30,
    forestSpread:
      newParticleData?.forestSpread || currentConfig.forestSpread || 40,
    pineColor:
      newParticleData?.pineColor ||
      currentConfig.pineColor ||
      FOLIAGE_TREE_COLOR_DEFAULTS.pine,
    oakColor:
      newParticleData?.oakColor ||
      currentConfig.oakColor ||
      FOLIAGE_TREE_COLOR_DEFAULTS.oak,
    birchColor:
      newParticleData?.birchColor ||
      currentConfig.birchColor ||
      FOLIAGE_TREE_COLOR_DEFAULTS.birch,
    grassColor:
      newParticleData?.grassColor ||
      currentConfig.grassColor ||
      newParticleData?.color ||
      currentConfig.color ||
      FOLIAGE_GRASS_COLOR_DEFAULT,
    bushColor:
      newParticleData?.bushColor ||
      currentConfig.bushColor ||
      newParticleData?.color ||
      currentConfig.color ||
      FOLIAGE_BUSH_COLOR_DEFAULT,
    color:
      newParticleData?.grassColor ||
      newParticleData?.color ||
      currentConfig.grassColor ||
      currentConfig.color ||
      FOLIAGE_GRASS_COLOR_DEFAULT,
    opacity:
      newParticleData?.opacity !== undefined
        ? newParticleData.opacity
        : currentConfig.opacity !== undefined
          ? currentConfig.opacity
          : 1.0,
    size:
      newParticleData?.size !== undefined
        ? Number(newParticleData.size)
        : currentConfig.size !== undefined
          ? Number(currentConfig.size)
          : 1.0,
    ...resolveFoliageAnimationConfig({
      ...currentConfig,
      ...newParticleData,
    }),
  };

  const previousAnimationConfig = resolveFoliageAnimationConfig(currentConfig);
  const nextAnimationConfig = resolveFoliageAnimationConfig(updatedConfig);
  const animationConfigChanged =
    previousAnimationConfig.windStrength !== nextAnimationConfig.windStrength ||
    previousAnimationConfig.swaySpeed !== nextAnimationConfig.swaySpeed ||
    previousAnimationConfig.rustleSpeed !== nextAnimationConfig.rustleSpeed ||
    previousAnimationConfig.waveSpeed !== nextAnimationConfig.waveSpeed;

  applyFoliageMaterialColors(foliageSystem, updatedConfig);

  // Update sizes if changed
  if (updatedConfig.size !== currentConfig.size) {
    const newSize = updatedConfig.size;
    foliageSystem.children.forEach((child) => {
      if (child.userData && child.userData.baseScale !== undefined) {
        const base = child.userData.baseScale;
        if (child.userData.type === 'grass') {
          child.scale.y = base * newSize;
        } else {
          child.scale.set(base * newSize, base * newSize, base * newSize);
        }
      }
    });
  }

  // Update transforms if provided
  if (newParticleData?.position) {
    foliageSystem.position.set(
      newParticleData.position.x !== undefined
        ? newParticleData.position.x
        : foliageSystem.position.x,
      newParticleData.position.y !== undefined
        ? newParticleData.position.y
        : foliageSystem.position.y,
      newParticleData.position.z !== undefined
        ? newParticleData.position.z
        : foliageSystem.position.z
    );
  }
  if (newParticleData?.rotation) {
    foliageSystem.rotation.set(
      newParticleData.rotation.x !== undefined
        ? newParticleData.rotation.x
        : foliageSystem.rotation.x,
      newParticleData.rotation.y !== undefined
        ? newParticleData.rotation.y
        : foliageSystem.rotation.y,
      newParticleData.rotation.z !== undefined
        ? newParticleData.rotation.z
        : foliageSystem.rotation.z
    );
  }
  if (newParticleData?.scale) {
    foliageSystem.scale.set(
      newParticleData.scale.x !== undefined
        ? newParticleData.scale.x
        : foliageSystem.scale.x,
      newParticleData.scale.y !== undefined
        ? newParticleData.scale.y
        : foliageSystem.scale.y,
      newParticleData.scale.z !== undefined
        ? newParticleData.scale.z
        : foliageSystem.scale.z
    );
  }

  // Update userData with new configuration
  foliageSystem.userData = {
    ...foliageSystem.userData,
    ...updatedConfig,
    material_settings: {
      ...foliageSystem.userData.material_settings,
      albedo: updatedConfig.color,
      opacity: updatedConfig.opacity,
    },
  };
  foliageSystem.userData.animateFunction =
    createFoliageAnimateFunction(foliageSystem);

  // Decide whether range or specific counts changed
  const oldTreeCount = currentConfig.treeCount ?? 10;
  const oldBushCount = currentConfig.bushCount ?? 10;
  const oldGrassCount = currentConfig.grassCount ?? 50;
  const oldRange = currentConfig.range ?? foliageSystem.userData.range ?? 10;

  const newTreeCount = updatedConfig.treeCount;
  const newBushCount = updatedConfig.bushCount;
  const newGrassCount = updatedConfig.grassCount;
  const newRange = updatedConfig.range;

  const rangeChanged = typeof newParticleData?.range !== 'undefined' && Number(newRange) !== Number(oldRange);
  const treeCountChanged = typeof newParticleData?.treeCount !== 'undefined' && newTreeCount !== oldTreeCount;
  const bushCountChanged = typeof newParticleData?.bushCount !== 'undefined' && newBushCount !== oldBushCount;
  const grassCountChanged = typeof newParticleData?.grassCount !== 'undefined' && newGrassCount !== oldGrassCount;

  // Use helpers attached at creation
  const createTree = foliageSystem.userData.createTree;
  const createBush = foliageSystem.userData.createBush;
  const createGrass = foliageSystem.userData.createGrass;

  const rVal = Number(updatedConfig.range) || oldRange || 10;
  const newAreaRadius = Math.max(1, rVal * 2);

  if (rangeChanged) {
    // Full rebuild when area/range changes (keeps behavior consistent)
    while (foliageSystem.children.length) {
      const child = foliageSystem.children[0];
      if (child.userData && typeof child.userData.cleanup === 'function') {
        try {
          child.userData.cleanup();
        } catch (e) {
          // ignore
        }
      }
      foliageSystem.remove(child);
    }

    // Recreate trees
    for (let i = 0; i < updatedConfig.treeCount; i++) {
      let treeType = ['pine', 'oak', 'birch'][Math.floor(Math.random() * 3)];
      const tree = createTree ? createTree(treeType, newParticleData) : null;
      if (!tree) continue;
      const angle = (i / Math.max(1, updatedConfig.treeCount)) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      const radius = Math.random() * newAreaRadius;
      const x = Math.cos(angle) * radius + (Math.random() - 0.5) * (newAreaRadius * 0.05);
      const z = Math.sin(angle) * radius + (Math.random() - 0.5) * (newAreaRadius * 0.05);
      tree.position.set(x, 0, z);
      tree.rotation.y = Math.random() * Math.PI * 2;
      setPlantBaseFacing(tree);
      const baseScale = 0.4 + Math.random() * 0.5;
      tree.userData.baseScale = baseScale;
      const finalScale = baseScale * updatedConfig.size;
      tree.scale.set(finalScale, finalScale, finalScale);
      foliageSystem.add(tree);
    }

    // Recreate bushes
    for (let i = 0; i < updatedConfig.bushCount; i++) {
      const bush = createBush ? createBush(newParticleData) : null;
      if (!bush) continue;
      bush.position.set((Math.random() - 0.5) * newAreaRadius * 1.2, 0, (Math.random() - 0.5) * newAreaRadius * 1.2);
      bush.rotation.y = Math.random() * Math.PI * 2;
      setPlantBaseFacing(bush);
      const baseScale = 0.2 + Math.random() * 0.3;
      bush.userData.baseScale = baseScale;
      bush.scale.setScalar(baseScale * updatedConfig.size);
      foliageSystem.add(bush);
    }

    // Recreate grass
    for (let i = 0; i < updatedConfig.grassCount; i++) {
      const grass = createGrass ? createGrass(newParticleData) : null;
      if (!grass) continue;
      grass.position.set((Math.random() - 0.5) * newAreaRadius * 1.8, 0, (Math.random() - 0.5) * newAreaRadius * 1.8);
      grass.rotation.y = Math.random() * Math.PI * 2;
      setPlantBaseFacing(grass);
      const baseScale = 0.3 + Math.random() * 0.4;
      grass.userData.baseScale = baseScale;
      grass.scale.y = baseScale * updatedConfig.size;
      foliageSystem.add(grass);
    }

    // Save updated range so subsequent updates use correct area
    foliageSystem.userData.range = rVal;
  } else {
    // Only adjust counts: add or remove items without changing existing positions

    // Trees
    if (treeCountChanged) {
      const delta = newTreeCount - oldTreeCount;
      if (delta > 0) {
        for (let i = 0; i < delta; i++) {
          const treeType = ['pine', 'oak', 'birch'][Math.floor(Math.random() * 3)];
          const tree = createTree ? createTree(treeType, newParticleData) : null;
          if (!tree) continue;
          const angle = Math.random() * Math.PI * 2;
          const radius = Math.random() * newAreaRadius;
          const x = Math.cos(angle) * radius + (Math.random() - 0.5) * (newAreaRadius * 0.05);
          const z = Math.sin(angle) * radius + (Math.random() - 0.5) * (newAreaRadius * 0.05);
          tree.position.set(x, 0, z);
          tree.rotation.y = Math.random() * Math.PI * 2;
          setPlantBaseFacing(tree);
          const baseScale = 0.4 + Math.random() * 0.5;
          tree.userData.baseScale = baseScale;
          const finalScale = baseScale * updatedConfig.size;
          tree.scale.set(finalScale, finalScale, finalScale);
          foliageSystem.add(tree);
        }
      } else if (delta < 0) {
        const trees = foliageSystem.children.filter((c) => c.userData && c.userData.type === 'tree');
        const removeCount = Math.min(Math.abs(delta), trees.length);
        for (let i = 0; i < removeCount; i++) {
          const rem = trees[trees.length - 1 - i];
          if (rem.userData && typeof rem.userData.cleanup === 'function') {
            try {
              rem.userData.cleanup();
            } catch (e) {
              // ignore
            }
          }
          foliageSystem.remove(rem);
        }
      }
    }

    // Bushes
    if (bushCountChanged) {
      const delta = newBushCount - oldBushCount;
      if (delta > 0) {
        for (let i = 0; i < delta; i++) {
          const bush = createBush ? createBush(newParticleData) : null;
          if (!bush) continue;
          bush.position.set((Math.random() - 0.5) * newAreaRadius * 1.2, 0, (Math.random() - 0.5) * newAreaRadius * 1.2);
          bush.rotation.y = Math.random() * Math.PI * 2;
          setPlantBaseFacing(bush);
          const baseScale = 0.2 + Math.random() * 0.3;
          bush.userData.baseScale = baseScale;
          bush.scale.setScalar(baseScale * updatedConfig.size);
          foliageSystem.add(bush);
        }
      } else if (delta < 0) {
        const bushes = foliageSystem.children.filter((c) => c.userData && c.userData.type === 'bush');
        const removeCount = Math.min(Math.abs(delta), bushes.length);
        for (let i = 0; i < removeCount; i++) {
          const rem = bushes[bushes.length - 1 - i];
          if (rem.userData && typeof rem.userData.cleanup === 'function') {
            try {
              rem.userData.cleanup();
            } catch (e) {
              // ignore
            }
          }
          foliageSystem.remove(rem);
        }
      }
    }

    // Grass
    if (grassCountChanged) {
      const delta = newGrassCount - oldGrassCount;
      if (delta > 0) {
        for (let i = 0; i < delta; i++) {
          const grass = createGrass ? createGrass(newParticleData) : null;
          if (!grass) continue;
          grass.position.set((Math.random() - 0.5) * newAreaRadius * 1.8, 0, (Math.random() - 0.5) * newAreaRadius * 1.8);
          grass.rotation.y = Math.random() * Math.PI * 2;
          setPlantBaseFacing(grass);
          const baseScale = 0.3 + Math.random() * 0.4;
          grass.userData.baseScale = baseScale;
          grass.scale.y = baseScale * updatedConfig.size;
          foliageSystem.add(grass);
        }
      } else if (delta < 0) {
        const grasses = foliageSystem.children.filter((c) => c.userData && c.userData.type === 'grass');
        const removeCount = Math.min(Math.abs(delta), grasses.length);
        for (let i = 0; i < removeCount; i++) {
          const rem = grasses[grasses.length - 1 - i];
          if (rem.userData && typeof rem.userData.cleanup === 'function') {
            try {
              rem.userData.cleanup();
            } catch (e) {
              // ignore
            }
          }
          foliageSystem.remove(rem);
        }
      }
    }

    // Keep saved range consistent
    foliageSystem.userData.range = rVal;
  }

  // Ensure stored counts match updated config
  foliageSystem.userData.treeCount = updatedConfig.treeCount;
  foliageSystem.userData.bushCount = updatedConfig.bushCount;
  foliageSystem.userData.grassCount = updatedConfig.grassCount;

  // Manage life timeout: clear previous then set new if provided
  try {
    clearLifeTimeout(foliageSystem);
    const lifeVal = newParticleData?.life ?? foliageSystem.userData?.life;
    const lifeSeconds =
      typeof lifeVal === 'number' ? lifeVal : parseFloat(lifeVal);
    if (!isNaN(lifeSeconds) && lifeSeconds > 0) {
      setLifeTimeout(foliageSystem, lifeSeconds);
      foliageSystem.visible = true;
      if (foliageSystem.userData) {
        foliageSystem.userData.isStopped = false;
        foliageSystem.userData.isPlaying = true;
      }
    }
  } catch (e) {
    // ignore
  }

  // Update system metadata if ID changed
  if (newParticleData?.id && newParticleData.id !== foliageSystem.name) {
    foliageSystem.name = newParticleData.id;
    foliageSystem.url = newParticleData.id;
  }


  if (foliageSystem.userData) foliageSystem.userData.forceBoxUpdate = true;

  if (animationConfigChanged) {
    restartFoliageAnimation(foliageSystem);
  }

  return foliageSystem;
};

// Helper function to find foliage system by name/id in scene
const findFoliageSystem = (scene, nameOrId) => {
  return scene.children.find(
    (child) =>
      (child.name === nameOrId || child.url === nameOrId) &&
      child.objType === 'foliage'
  );
};

// Convenience function to update foliage by name
const updateFoliageByName = (scene, nameOrId, newParticleData) => {
  const foliageSystem = findFoliageSystem(scene, nameOrId);
  if (foliageSystem) {
    return updateFoliage(scene, foliageSystem, newParticleData);
  } else {
    console.warn(
      `Foliage system with name/id "${nameOrId}" not found in scene`
    );
    return null;
  }
};

export default handleFoliage;
export { updateFoliage, updateFoliageByName };
